<template>
  <div class="allocation-options">
    <!-- Sequential Execution -->
    <div
      class="allocation-card"
      :class="{ selected: allocationMode === 1 }"
      @click="handleChange(1)"
    >
      <div class="card-check" v-if="allocationMode === 1">
        <a-icon type="check-circle" theme="filled" />
      </div>
      <div class="card-icon sequential">🎯</div>
      <h4 class="card-title">{{ $t("SequentialExecution") }}</h4>
      <p class="card-desc">{{ $t("SequentialExecutionDesc") }}</p>
      <div class="how-it-works">
        <span class="works-label">{{ $t("HowItWorks") }}:</span>
        <div class="campaign-flow">
          <div class="flow-item active">
            <div class="flow-name">{{ $t("campaignA") }}</div>
            <div class="flow-limit">{{dailyLimit}}{{ $t("perDay") }}</div>
            <div class="flow-status">{{ $t("running") }}</div>
          </div>
          <span class="flow-arrow">{{ $t("then") }}</span>
          <div class="flow-item waiting">
            <div class="flow-name">{{ $t("campaignB") }}</div>
            <div class="flow-limit">{{dailyLimit}}{{ $t("perDay") }}</div>
            <div class="flow-status">{{ $t("waiting") }}</div>
          </div>
          <span class="flow-arrow">{{ $t("then") }}</span>
          <div class="flow-item waiting">
            <div class="flow-name">{{ $t("campaignC") }}</div>
            <div class="flow-limit">{{dailyLimit}}{{ $t("perDay") }}</div>
            <div class="flow-status">{{ $t("waiting") }}</div>
          </div>
        </div>
      </div>
      <div class="warning-tip">
        <a-icon type="exclamation-circle" />
        {{ $t("SequentialWarning") }}
      </div>
    </div>

    <!-- Equal Distribution -->
    <div
      class="allocation-card"
      :class="{ selected: allocationMode === 2 }"
      @click="handleChange(2)"
    >
      <div class="card-check" v-if="allocationMode === 2">
        <a-icon type="check-circle" theme="filled" />
      </div>
      <div class="card-icon equal">⚖️</div>
      <h4 class="card-title">{{ $t("EqualDistribution") }}</h4>
      <p class="card-desc">{{ $t("EqualDistributionDesc") }}</p>
      <div class="how-it-works">
        <span class="works-label">{{ $t("HowItWorks") }}:</span>
        <div class="equal-stats">
          <div class="equal-row">
            <span>{{ $t("DailyLimit") }}:</span>
            <strong>{{ dailyLimit }} {{ $t("requests") }}</strong>
          </div>
          <div class="equal-row">
            <span>{{ $t("ActiveCampaigns") }}:</span>
            <strong>{{ activeCampaignsCount }} {{ $t("campaigns") }}</strong>
          </div>
          <div class="equal-row highlight">
            <span>{{ $t("EachCampaignGets") }}:</span>
            <strong class="highlight-text"
              >~{{activeCampaignsCount? Math.floor(dailyLimit/activeCampaignsCount):dailyLimit}} {{ $t("requestsPerDay") }}</strong
            >
          </div>
        </div>
      </div>
      <div class="info-tip">
        <!-- <a-icon type="info-circle" /> -->
        <span>💡</span>
        {{ $t("EqualInfo") }}
      </div>
    </div>
  </div>
</template>

<script>
import i18n from "./i18n";

export default {
  name: "AllocationOptions",
  i18n,
  props: {
    allocationMode: {
      type: [Number, String],
      default: 1,
      validator: (value) => [1, 2].includes(value),
    },
    dailyLimit: {
      type: Number,
      default: 25,
    },
    activeCampaignsCount: {
      type: Number,
      default: 0,
    },
  },
  methods: {
    handleChange(mode) {
      this.$emit("change", mode);
    },
  },
};
</script>

<style scoped lang="less">
.allocation-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.allocation-card {
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;

  &:hover {
    border-color: #12b981;
  }

  &.selected {
    border-color: #12b981;
    background: #e8feef;
  }

  .card-check {
    position: absolute;
    top: 12px;
    right: 12px;
    color: #12b981;
    font-size: 20px;
  }

  .card-icon {
    font-size: 24px;
    margin-bottom: 12px;

    &.sequential {
      color: #0e756a;
    }

    &.equal {
      color: #1890ff;
    }
  }

  .card-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #1f1f1f;
  }

  .card-desc {
    font-size: 13px;
    color: #666;
    margin-bottom: 16px;
    line-height: 1.5;
  }

  .how-it-works {
    background: #fff;
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid #e6e8eb;

    .works-label {
      font-size: 11px;
      color: #999;
      text-transform: uppercase;
      display: block;
      margin-bottom: 8px;
    }
  }

  .campaign-flow {
    display: flex;
    align-items: center;
    gap: 8px;

    .flow-item {
      text-align: center;
      padding: 8px 12px;
      border-radius: 8px;
      background: #f5f5f5;
      border: 1px solid #12b981;

      &.active {
        background: #d1fae6;
        color: #1f2937;
      }

      .flow-name {
        font-size: 11px;
        font-weight: 500;
      }

      .flow-limit {
        font-size: 12px;
        margin: 2px 0;
        color: #079669;
        font-weight: bold;
      }

      .flow-status {
        font-size: 10px;
        opacity: 0.8;
        color: #12b981;
      }

      &.waiting {
        background: #f7f7f9;
        border-color: #e2e3e6;

        .flow-limit {
          color: #999;
        }

        .flow-status {
          color: #999;
        }
      }
    }

    .flow-arrow {
      color: #9da3af;
      font-size: 12px;
      font-weight: bold;
    }
  }

  .equal-stats {
    .equal-row {
      display: flex;
      justify-content: space-between;
      padding: 6px 0;
      font-size: 13px;
      border-bottom: 1px dashed #e8e8e8;

      &:last-child {
        border-bottom: none;
      }

      &.highlight {
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid #e8e8e8;

        .highlight-text {
          color: #0e756a;
          font-size: 14px;
        }
      }
    }
  }

  .warning-tip {
    background: #fff5c7;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 12px;
    color: #92400d;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .info-tip {
    background: #f0f6ff;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 12px;
    color: #1f40af;
    display: flex;
    align-items: center;
    gap: 6px;
  }
}
</style>
